/**
 * M07 批量回归 shell · S2 Day 8b
 *
 * 三档 mode + 跑回归 + 命中率卡片 + 混淆矩阵 + 单样本失败定位
 * 数据源：useEvalSamples（GET）+ useRegress（POST）
 */
import { ArrowRight, CheckCircle2, ClipboardList, ShieldCheck } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DEFAULT_EVAL_MERCHANT_ID, useCreateEvalRun, useEvalSamples } from "@/lib/api/eval";
import type { EvalRunCreateRequest, EvalRunResponse, RegressionResponse } from "@/lib/api/eval";
import { sampleLabel, targetStageLabel } from "@/lib/product-labels";

import { ConfusionMatrixHeatmap } from "./confusion-matrix-heatmap";
import { CxScorePanel } from "./cx-score-panel";
import {
	AbExperimentGovernancePanel,
	ScenarioMetricsPanel,
	VersionResponsibilityChainPanel,
} from "./eval-governance-panels";
import {
	BadcaseLedger,
	EvalRunComparePanel,
	EvalRunLedger,
	RegressionBatchPanel,
} from "./eval-ledgers";
import { Golden15Panel } from "./golden15-panel";
import { HitRateCards } from "./hit-rate-cards";
import { PerSampleTable } from "./per-sample-table";
import { RegressTrigger } from "./regress-trigger";

const SINGLE_STAGE_MANUAL_CANDIDATES = [
	{
		stageId: "intent_classification",
		label: "意图分类",
		sampleId: "P10TA001",
		source: "页面会话级 actual result：actual_intent、confidence、规则命中。",
		review: "对照 expected_intent vs actual_intent，先判断是否可复现。",
		failure: "wrong_intent",
		asset: "L1 Intent、intent_rule.yaml、intent whitelist",
		next: "人工确认后再进入 Badcase 流转。",
	},
	{
		stageId: "playbook_routing",
		label: "处理方案",
		sampleId: "P10TA001",
		source: "页面会话级 actual result：recommended_playbook.id、match_mode。",
		review: "对照 expected_pb vs actual_playbook_id，先判断方案映射是否错误。",
		failure: "wrong_playbook",
		asset: "分类到方案映射、方案配置、兜底回复",
		next: "人工确认后回到处理方案或映射修复。",
	},
	{
		stageId: "tool_and_evidence",
		label: "工具与证据",
		sampleId: "P10TA005",
		source: "页面会话级结果：授权检查、知识检索沙盒、命中数量和审计 ID。",
		review: "对照 expected_audit vs 工具/知识结果，先判断证据是否缺失或未使用。",
		failure: "evidence_not_used、skill_not_triggered、tool_error",
		asset: "工具、知识、证据注入、写操作保护",
		next: "人工确认后进入能力修复或样本补齐。",
	},
	{
		stageId: "knowledge_retrieval",
		label: "知识与证据",
		sampleId: "P10TA005",
		source: "页面会话级 actual result：knowledge_hits、source、audit_id。",
		review: "对照 expected_evidence_refs vs knowledge_hits/audit_id，先判断知识是否命中或可复现。",
		failure: "missing_evidence、knowledge_miss、evidence_not_used",
		asset: "政策知识库、知识检索、证据注入",
		next: "人工确认后进入知识维护或证据注入修复。",
	},
	{
		stageId: "prompt_response",
		label: "提示词与话术",
		sampleId: "P10TA001",
		source:
			"页面会话级 actual result：generation_completed、prompt_snapshot、evidence_refs、final_answer。",
		review:
			"对照 expected_prompt_checks vs prompt_snapshot/evidence_refs/final_answer，先判断证据使用和话术边界是否成立。",
		failure: "expected_prompt_checks、unsupported_claim、missing_clarification",
		asset: "主回复提示词、证据注入、方案引导",
		next: "人工确认后进入提示词与话术或方案修复；不自动提交 EvalRun。",
	},
	{
		stageId: "quality_guard",
		label: "质量策略",
		sampleId: "P10TA007",
		source: "页面会话级 actual result：quality_completed、quality_mode、final_action_type。",
		review:
			"对照 expected_quality_decision vs quality_completed.output.decision，先判断 PASS/REVISE/BLOCK/HANDOFF 是否成立。",
		failure: "expected_quality_decision、unsafe_action、quality_miss",
		asset: "质量策略、判断规则、R2 写操作保护",
		next: "人工确认后进入质量策略或写操作保护修复；不自动提交 EvalRun。",
	},
] as const;

const SINGLE_STAGE_SUBMIT_READINESS_ROWS = [
	{
		stageId: "intent_classification",
		label: "意图分类",
		sampleId: "P10TA001",
		readinessState: "submit_ready_candidate",
		confirmations:
			"确认 actual_result_ref 可追溯；确认 expected_intent 与 actual_intent 差异成立。",
		transition: "draft -> manual_reviewed -> submit_ready",
		asset: "asset:intent_classifier、intent_rule.yaml、intent whitelist",
		next: "人工确认后可进入 submit-ready 候选；仍不调用 EvalRun 提交接口。",
	},
	{
		stageId: "playbook_routing",
		label: "处理方案",
		sampleId: "P10TA001",
		readinessState: "submit_ready_candidate",
		confirmations: "确认 expected_pb 与 actual_playbook_id 差异成立；确认映射关系需要修复。",
		transition: "draft -> manual_reviewed -> submit_ready",
		asset: "playbook:PB02_refund、intent_playbook_mapping.yaml",
		next: "人工确认后可进入 submit-ready 候选；仍不调用 EvalRun 提交接口。",
	},
	{
		stageId: "tool_and_evidence",
		label: "工具与证据",
		sampleId: "P10TA005",
		readinessState: "blocked_by_missing_evidence",
		confirmations: "确认 expected_audit 与工具/知识结果差异成立；补齐 Trace 字段后再评估。",
		transition: "draft -> manual_reviewed",
		asset: "知识检索（SK05）、政策知识库、证据注入",
		next: "缺少可自动判分的工具/知识 Trace 字段，先保持阻塞。",
	},
	{
		stageId: "knowledge_retrieval",
		label: "知识与证据",
		sampleId: "P10TA005",
		readinessState: "blocked_by_stage_contract",
		confirmations:
			"确认 expected_evidence_refs 与 knowledge_hits/audit_id 差异成立；先保留人工复核口径。",
		transition: "draft -> manual_reviewed",
		asset: "政策知识库、知识检索、证据注入",
		next: "知识检索暂未接入 EvalRun 自动判分合同，先保持阻塞。",
	},
	{
		stageId: "prompt_response",
		label: "提示词与话术",
		sampleId: "P10TA001",
		readinessState: "blocked_by_stage_contract",
		confirmations:
			"确认 expected_prompt_checks 与 prompt_snapshot/evidence_refs/final_answer 差异成立；先保留人工复核口径。",
		transition: "draft -> manual_reviewed",
		asset: "主回复提示词、证据注入、方案引导",
		next: "提示词结果先保留人工复核，等独立评测口径或 expected_prompt_checks 判分合同后再提交。",
	},
	{
		stageId: "quality_guard",
		label: "质量策略",
		sampleId: "P10TA007",
		readinessState: "blocked_by_stage_contract",
		confirmations:
			"确认 expected_quality_decision 与 quality_completed.output.decision 差异成立；先保留人工复核口径。",
		transition: "draft -> manual_reviewed",
		asset: "质量策略、判断规则、R2 写操作保护",
		next: "质量策略结果先保留人工复核，等质量判分合同稳定后再提交。",
	},
] as const;

const SINGLE_STAGE_SUBMIT_CONTRACT_ROWS = [
	{
		stageId: "intent_classification",
		label: "意图分类",
		sampleId: "P10TA001",
		submitState: "request_ready_for_manual_submit",
		request:
			"EvalRunCreateRequest；dataset_id=p10_tool_audit_candidates；mode=intent；data_mode=mock",
		review: "确认提交门禁已人工通过；确认请求只包含当前单环节样本。",
		asset: "asset:intent_classifier、intent_rule.yaml",
		next: "人工确认后可由后续真实提交动作调用现有 EvalRun 接口。",
	},
	{
		stageId: "playbook_routing",
		label: "处理方案",
		sampleId: "P10TA001",
		submitState: "request_ready_for_manual_submit",
		request:
			"EvalRunCreateRequest；dataset_id=p10_tool_audit_candidates；mode=playbook；data_mode=mock",
		review: "确认 expected_pb 与 actual_playbook_id 差异成立；确认映射或方案责任。",
		asset: "playbook:PB02_refund、intent_playbook_mapping.yaml",
		next: "人工确认后可由后续真实提交动作调用现有 EvalRun 接口。",
	},
	{
		stageId: "tool_and_evidence",
		label: "工具与证据",
		sampleId: "P10TA005",
		submitState: "blocked_by_missing_evidence",
		request: "不生成 EvalRunCreateRequest；缺少工具/知识 Trace 自动判分字段。",
		review: "先补 expected_audit、工具结果、知识命中和证据使用字段。",
		asset: "知识检索（SK05）、政策知识库、证据注入",
		next: "当前阻塞，不进入提交请求合同。",
	},
	{
		stageId: "knowledge_retrieval",
		label: "知识与证据",
		sampleId: "P10TA005",
		submitState: "blocked_by_stage_contract",
		request: "不生成 EvalRunCreateRequest；知识检索结果先保留人工复核。",
		review: "先补 expected_evidence_refs、knowledge_hits、audit_id 和证据使用字段。",
		asset: "政策知识库、知识检索、证据注入",
		next: "当前阻塞，不进入提交请求合同。",
	},
	{
		stageId: "prompt_response",
		label: "提示词与话术",
		sampleId: "P10TA001",
		submitState: "blocked_by_stage_contract",
		request: "不生成 EvalRunCreateRequest；提示词结果先保留人工复核。",
		review: "先补 expected_prompt_checks 判分口径，再评估是否接入 EvalRun。",
		asset: "主回复提示词、证据注入、方案引导",
		next: "当前阻塞，不进入提交请求合同。",
	},
	{
		stageId: "quality_guard",
		label: "质量策略",
		sampleId: "P10TA007",
		submitState: "blocked_by_stage_contract",
		request: "不生成 EvalRunCreateRequest；质量策略结果先保留人工复核。",
		review: "先补 expected_quality_decision 判分口径和质量策略责任资产，再评估是否接入 EvalRun。",
		asset: "质量策略、判断规则、R2 写操作保护",
		next: "当前阻塞，不进入提交请求合同。",
	},
] as const;

const P10_SINGLE_STAGE_DATASET_ID = "p10_tool_audit_candidates";
const P10_SINGLE_STAGE_DATASET_VERSION = "2026-05-15-s1u";

const P10_SINGLE_STAGE_SAMPLES = {
	P10TA001: {
		id: "P10TA001",
		message: "我要退款，但是忘记订单号了",
		expected_intent: "refund_request",
		expected_pb: "PB02_refund",
	},
} as const;

function buildSingleStageEvalRunRequest({
	mode,
	stageLabel,
	asset,
}: {
	mode: EvalRunCreateRequest["mode"];
	stageLabel: string;
	asset: string;
}): EvalRunCreateRequest {
	return {
		merchant_id: DEFAULT_EVAL_MERCHANT_ID,
		run_name: `单环节人工提交：${stageLabel} / P10TA001`,
		run_type: "regression",
		mode,
		samples: [P10_SINGLE_STAGE_SAMPLES.P10TA001],
		dataset_id: P10_SINGLE_STAGE_DATASET_ID,
		dataset_version: P10_SINGLE_STAGE_DATASET_VERSION,
		data_mode: "mock",
		comparison_role: "standalone",
		baseline_run_id: null,
		created_by: "admin-console",
		notes: `人工提交回执；target_stage=${stageLabel}；asset=${asset}；no_auto_badcase；no_regression_writeback`,
	};
}

const SINGLE_STAGE_MANUAL_SUBMIT_GATE_ROWS = [
	{
		stageId: "intent_classification",
		label: "意图分类",
		sampleId: "P10TA001",
		submitGateState: "manual_submit_allowed",
		endpoint: "POST /admin/v1/eval/runs",
		transition: "request_ready_for_manual_submit -> submitted -> eval_run_completed",
		check:
			"EvalRun 返回 run_id；mode=intent；样本数仍为 1；失败样本只进入评测结果，不自动生成 Badcase。",
		asset: "意图分类器、分类规则",
		next: "人工确认后可调用现有 EvalRun 接口；不写回回归集。",
		submitRequest: buildSingleStageEvalRunRequest({
			mode: "intent",
			stageLabel: "意图分类",
			asset: "意图分类器、分类规则",
		}),
	},
	{
		stageId: "playbook_routing",
		label: "处理方案",
		sampleId: "P10TA001",
		submitGateState: "manual_submit_allowed",
		endpoint: "POST /admin/v1/eval/runs",
		transition: "request_ready_for_manual_submit -> submitted -> eval_run_completed",
		check:
			"EvalRun 返回 run_id；mode=playbook；样本数仍为 1；失败样本只进入评测结果，不自动生成 Badcase。",
		asset: "退款处理方案（PB02_refund）、分类到方案映射",
		next: "人工确认后可调用现有 EvalRun 接口；不写回回归集。",
		submitRequest: buildSingleStageEvalRunRequest({
			mode: "playbook",
			stageLabel: "处理方案",
			asset: "退款处理方案（PB02_refund）、分类到方案映射",
		}),
	},
	{
		stageId: "tool_and_evidence",
		label: "工具与证据",
		sampleId: "P10TA005",
		submitGateState: "blocked_by_missing_evidence",
		endpoint: "不调用",
		transition: "request_ready_for_manual_submit 前阻塞",
		check: "先补 expected_audit、工具结果、知识命中和证据使用字段。",
		asset: "知识检索（SK05）、政策知识库、证据注入",
		next: "当前阻塞，不进入人工提交执行门。",
	},
	{
		stageId: "knowledge_retrieval",
		label: "知识与证据",
		sampleId: "P10TA005",
		submitGateState: "blocked_by_stage_contract",
		endpoint: "不调用",
		transition: "manual_reviewed 后阻塞",
		check: "先补 expected_evidence_refs、knowledge_hits、audit_id 和证据使用字段。",
		asset: "政策知识库、知识检索、证据注入",
		next: "当前阻塞，不进入人工提交执行门。",
	},
	{
		stageId: "prompt_response",
		label: "提示词与话术",
		sampleId: "P10TA001",
		submitGateState: "blocked_by_stage_contract",
		endpoint: "不调用",
		transition: "manual_reviewed 后阻塞",
		check: "先补 expected_prompt_checks 判分口径，再评估是否接入 EvalRun。",
		asset: "主回复提示词、证据注入、方案引导",
		next: "提示词结果先保留人工复核，不进入人工提交执行门。",
	},
	{
		stageId: "quality_guard",
		label: "质量策略",
		sampleId: "P10TA007",
		submitGateState: "blocked_by_stage_contract",
		endpoint: "不调用",
		transition: "manual_reviewed 后阻塞",
		check: "先补 expected_quality_decision 判分口径和质量策略责任资产，再评估是否接入 EvalRun。",
		asset: "质量策略、判断规则、R2 写操作保护",
		next: "质量策略结果先保留人工复核，不进入人工提交执行门。",
	},
] as const;

const SINGLE_STAGE_BADCASE_DECISION_ROWS = [
	{
		stageId: "intent_classification",
		label: "意图分类",
		sampleId: "P10TA001",
		decisionState: "manual_badcase_review_required",
		evidence: "评测运行已完成；run_id 可回看；本次单环节结果已带入。",
		review: "人工确认失败可复现、差异成立、责任资产为意图分类。",
		next: "人工确认后才进入现有 Badcase 流转。",
	},
	{
		stageId: "playbook_routing",
		label: "处理方案",
		sampleId: "P10TA001",
		decisionState: "manual_badcase_review_required",
		evidence: "评测运行已完成；run_id 可回看；提交前检查和请求预览可追溯。",
		review: "人工确认方案映射或处理方案责任成立。",
		next: "人工确认后才进入现有 Badcase 流转。",
	},
	{
		stageId: "tool_and_evidence",
		label: "工具与证据",
		sampleId: "P10TA005",
		decisionState: "blocked_by_missing_evidence",
		evidence: "工具/知识 Trace 字段和自动判分证据仍不足。",
		review: "先补 expected_audit、工具结果、知识命中和证据使用字段。",
		next: "暂不进入 Badcase 决策门。",
	},
	{
		stageId: "knowledge_retrieval",
		label: "知识与证据",
		sampleId: "P10TA005",
		decisionState: "blocked_by_stage_contract",
		evidence: "知识检索结果尚未接入 EvalRun 自动判分合同。",
		review: "先补 expected_evidence_refs、knowledge_hits、audit_id 和证据使用字段。",
		next: "暂不进入 Badcase 决策门。",
	},
	{
		stageId: "prompt_response",
		label: "提示词与话术",
		sampleId: "P10TA001",
		decisionState: "blocked_by_stage_contract",
		evidence: "提示词结果尚未接入 EvalRun 自动判分合同。",
		review: "先补 expected_prompt_checks 判分口径，再判断是否进入 Badcase。",
		next: "暂不进入 Badcase 决策门。",
	},
	{
		stageId: "quality_guard",
		label: "质量策略",
		sampleId: "P10TA007",
		decisionState: "blocked_by_stage_contract",
		evidence: "质量策略结果尚未接入 EvalRun 自动判分合同。",
		review: "先补 expected_quality_decision 判分口径，再判断是否进入 Badcase。",
		next: "暂不进入 Badcase 决策门。",
	},
] as const;

const SINGLE_STAGE_BADCASE_AUDIT_EVENTS = [
	{
		eventName: "badcase_decision.review_requested",
		trigger: "EvalRun 完成后进入人工决策门",
		requiredFields: "eval_run_id、sample_id、target_stage、responsible_asset",
		owner: "评测运营",
	},
	{
		eventName: "badcase_decision.confirmed",
		trigger: "人工确认失败可复现且责任资产成立",
		requiredFields: "reviewer、decision_reason、failure_type、responsible_asset",
		owner: "问题闭环",
	},
	{
		eventName: "badcase_decision.rejected",
		trigger: "人工确认不进入 Badcase 流转",
		requiredFields: "reviewer、reject_reason、next_action",
		owner: "评测运营",
	},
] as const;

function singleStageSelectionLabel(sampleId: string, stageLabel: string): string {
	return `已带入 ${sampleLabel(sampleId)} / ${stageLabel}`;
}

type SingleStageSelection = {
	sampleId: string | null;
	targetStage: string | null;
	source: string | null;
	candidateMode: string | null;
	draftMode: string | null;
	readinessMode: string | null;
	submitMode: string | null;
	executionMode: string | null;
};

export type M07View =
	| "scenario-metrics"
	| "eval-run"
	| "cx-score"
	| "badcase-loop"
	| "experiment-governance";

export function M07Shell({ view = "eval-run" }: { view?: M07View }) {
	const [result, setResult] = useState<RegressionResponse | null>(null);
	const samples = useEvalSamples();
	const singleStageSelection = getSingleStageManualCandidateSelection();
	const shouldShowSingleStagePanels =
		singleStageSelection.source === "p10_single_stage" ||
		(typeof window !== "undefined" && window.location.hash.startsWith("#single-stage"));

	return (
		<div className="space-y-4" data-testid="m07-shell">
			{view === "scenario-metrics" ? <ScenarioMetricsPanel /> : null}

			{view === "eval-run" ? (
				<>
					<EvalSampleStatus samples={samples} />
					<section>
						<RegressTrigger onResult={setResult} />
					</section>
					<Golden15Panel />
					{shouldShowSingleStagePanels ? (
						<>
							<SingleStageManualCandidatePanel selection={singleStageSelection} />
							<SingleStageEvalRunDraftPanel selection={singleStageSelection} />
							<SingleStageSubmitReadinessPanel selection={singleStageSelection} />
							<SingleStageSubmitContractPanel selection={singleStageSelection} />
							<SingleStageSubmitGatePanel selection={singleStageSelection} />
						</>
					) : null}
					<EvalRunLedger />
					{result === null ? (
						<div
							className="rounded-md border border-dashed border-surface-border bg-surface-card p-8 text-center text-sm text-text-muted"
							data-testid="m07-empty"
						>
							点击「跑批量回归」查看意图 / 方案命中率 + 混淆矩阵 + 失败样本定位
						</div>
					) : (
						<>
							<HitRateCards result={result} />
							<ConfusionMatrixHeatmap cells={result.confusion_matrix} />
							<PerSampleTable rows={result.per_sample} />
						</>
					)}
				</>
			) : null}

			{view === "cx-score" ? <CxScorePanel /> : null}

			{view === "badcase-loop" ? (
				<section className="space-y-4">
					<BadcaseLedger />
					<RegressionBatchPanel />
				</section>
			) : null}

			{view === "experiment-governance" ? (
				<>
					<VersionResponsibilityChainPanel />
					<EvalRunComparePanel />
					<AbExperimentGovernancePanel />
				</>
			) : null}
		</div>
	);
}

function EvalSampleStatus({ samples }: { samples: ReturnType<typeof useEvalSamples> }) {
	return (
		<div className="flex items-center justify-between rounded-md border border-surface-border bg-surface-card px-4 py-3">
			<div>
				<div className="text-sm font-semibold text-text-primary">回归样本口径</div>
				<p className="mt-1 text-sm text-text-secondary">
					先跑批量回归，再看运行历史和失败样本是否需要进入问题闭环。
				</p>
			</div>
			<div className="text-right text-xs text-text-muted">
				{samples.isLoading ? (
					"加载金样本…"
				) : samples.isError ? (
					<span className="text-brand-danger">金样本加载失败</span>
				) : (
					<>
						金样本 v<span className="font-mono">{samples.data?.version}</span>
						<span className="ml-2">总数 {samples.data?.total ?? 0}</span>
					</>
				)}
			</div>
		</div>
	);
}

function SingleStageManualCandidatePanel({
	selection,
}: {
	selection: SingleStageSelection;
}) {
	const selectedCandidate = SINGLE_STAGE_MANUAL_CANDIDATES.find(
		(candidate) =>
			selection.source === "p10_single_stage" &&
			selection.candidateMode === "manual" &&
			candidate.sampleId === selection.sampleId &&
			candidate.stageId === selection.targetStage,
	);

	return (
		<Card
			id="single-stage-candidate"
			className="scroll-mt-6"
			data-testid="m07-single-stage-manual-candidates"
		>
			<CardHeader className="space-y-2">
				<div className="flex items-start gap-3">
					<span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-surface-subtle text-brand-primary">
						<ClipboardList className="h-4 w-4" aria-hidden="true" />
					</span>
					<div>
						<div className="flex flex-wrap items-center gap-2">
							<CardTitle className="text-lg">单环节人工候选</CardTitle>
							<span className="rounded border border-brand-info/30 bg-brand-info/10 px-1.5 py-0.5 text-[11px] font-semibold text-brand-info">
								人工确认
							</span>
							<span className="rounded border border-surface-border bg-surface-card px-1.5 py-0.5 text-[11px] font-semibold text-text-secondary">
								六类环节已覆盖
							</span>
						</div>
						<CardDescription className="mt-1 leading-6">
							从链路测试台带入六类可运行单环节的页面会话级 actual result，先做人工复核；不自动提交
							EvalRun，不自动创建 Badcase。
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="rounded-md border border-surface-border bg-surface-subtle px-3 py-3 text-sm text-text-secondary">
					<div className="font-medium text-text-primary">
						{selectedCandidate
							? singleStageSelectionLabel(selectedCandidate.sampleId, selectedCandidate.label)
							: "等待从单环节结果带入"}
					</div>
					<p className="mt-2 leading-6">
						页面会话级 actual result 只作为人工候选线索；产品/运营确认失败可复现后，再进入 Badcase
						流转和后续回归写回。
					</p>
				</div>

				<div className="overflow-x-auto rounded-md border border-surface-border">
					<table className="w-full min-w-[1040px] border-collapse text-sm">
						<thead className="bg-surface-subtle text-left text-xs text-text-muted">
							<tr>
								<th className="px-3 py-2 font-medium">环节</th>
								<th className="px-3 py-2 font-medium">样本</th>
								<th className="px-3 py-2 font-medium">结果来源</th>
								<th className="px-3 py-2 font-medium">人工复核</th>
								<th className="px-3 py-2 font-medium">失败类型</th>
								<th className="px-3 py-2 font-medium">责任资产建议</th>
								<th className="px-3 py-2 font-medium">下一步</th>
							</tr>
						</thead>
						<tbody>
							{SINGLE_STAGE_MANUAL_CANDIDATES.map((candidate) => {
								const active = selectedCandidate?.stageId === candidate.stageId;
								return (
									<tr
										key={candidate.stageId}
										className={`border-t border-surface-border align-top ${
											active ? "bg-brand-info/10" : ""
										}`}
										data-testid="m07-single-stage-manual-candidate-row"
									>
										<td className="px-3 py-3">
											<div className="font-medium text-text-primary">{candidate.label}</div>
											<div className="mt-1 font-mono text-xs text-text-muted">
												{candidate.stageId}
											</div>
										</td>
										<td className="px-3 py-3 text-xs text-text-secondary">
											{sampleLabel(candidate.sampleId)}
										</td>
										<td className="px-3 py-3 text-text-secondary">{candidate.source}</td>
										<td className="px-3 py-3 text-text-secondary">{candidate.review}</td>
										<td className="px-3 py-3 text-text-secondary">{candidate.failure}</td>
										<td className="px-3 py-3 text-text-secondary">{candidate.asset}</td>
										<td className="px-3 py-3 text-text-secondary">{candidate.next}</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>

				<div className="flex flex-col gap-2 rounded-md border border-surface-border bg-surface-background px-3 py-3 text-sm text-text-secondary md:flex-row md:items-center md:justify-between">
					<span>
						边界：不自动提交 EvalRun、不自动创建
						Badcase、不新增接口或数据库；当前只做人工承接和审核入口。
					</span>
					<a
						href="/test-datasets?mock=1#p10-single-stage-manual-candidates"
						className="inline-flex w-fit items-center gap-1 rounded-md border border-surface-border bg-surface-card px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
					>
						回到样本资产口径
						<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
					</a>
				</div>
			</CardContent>
		</Card>
	);
}

function SingleStageEvalRunDraftPanel({
	selection,
}: {
	selection: SingleStageSelection;
}) {
	const selectedCandidate = SINGLE_STAGE_MANUAL_CANDIDATES.find(
		(candidate) =>
			selection.source === "p10_single_stage" &&
			selection.candidateMode === "manual" &&
			selection.draftMode === "eval_run" &&
			candidate.sampleId === selection.sampleId &&
			candidate.stageId === selection.targetStage,
	);
	const lifecycle =
		"draft -> manual_reviewed -> submit_ready -> submitted -> eval_run_completed -> badcase_decision";

	return (
		<Card
			id="single-stage-evalrun-draft"
			className="scroll-mt-6"
			data-testid="m07-single-stage-evalrun-draft"
		>
			<CardHeader className="space-y-2">
				<div className="flex items-start gap-3">
					<span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-surface-subtle text-brand-primary">
						<ClipboardList className="h-4 w-4" aria-hidden="true" />
					</span>
					<div>
						<div className="flex flex-wrap items-center gap-2">
							<CardTitle className="text-lg">评测运行草案</CardTitle>
							<span className="rounded border border-brand-warning/30 bg-brand-warning/10 px-1.5 py-0.5 text-[11px] font-semibold text-brand-warning">
								can_submit_eval_run=false
							</span>
						</div>
						<CardDescription className="mt-1 leading-6">
							承接单环节人工候选，先展示评测运行请求草案、状态生命周期和人工门禁；不自动提交
							EvalRun，不自动生成 Badcase。
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="rounded-md border border-surface-border bg-surface-subtle px-3 py-3 text-sm text-text-secondary">
					<div className="font-medium text-text-primary">
						{selectedCandidate
							? singleStageSelectionLabel(selectedCandidate.sampleId, selectedCandidate.label)
							: "等待从单环节人工候选带入"}
					</div>
					<p className="mt-2 leading-6">
						EvalRun 请求草案只保留 dataset_id、sample_id、target_stage、failure_type、
						responsible_asset 和 actual_result_ref 等必要字段；submitted、Badcase
						决策和回归写回继续留给后续合同。
					</p>
				</div>

				<div className="overflow-x-auto rounded-md border border-surface-border">
					<table className="w-full min-w-[1120px] border-collapse text-sm">
						<thead className="bg-surface-subtle text-left text-xs text-text-muted">
							<tr>
								<th className="px-3 py-2 font-medium">环节</th>
								<th className="px-3 py-2 font-medium">样本</th>
								<th className="px-3 py-2 font-medium">EvalRun 请求草案</th>
								<th className="px-3 py-2 font-medium">状态生命周期</th>
								<th className="px-3 py-2 font-medium">失败类型</th>
								<th className="px-3 py-2 font-medium">责任资产建议</th>
							</tr>
						</thead>
						<tbody>
							{SINGLE_STAGE_MANUAL_CANDIDATES.map((candidate) => {
								const active = selectedCandidate?.stageId === candidate.stageId;
								return (
									<tr
										key={candidate.stageId}
										className={`border-t border-surface-border align-top ${
											active ? "bg-brand-warning/10" : ""
										}`}
										data-testid="m07-single-stage-evalrun-draft-row"
									>
										<td className="px-3 py-3">
											<div className="font-medium text-text-primary">{candidate.label}</div>
											<div className="mt-1 font-mono text-xs text-text-muted">
												{candidate.stageId}
											</div>
										</td>
										<td className="px-3 py-3 text-xs text-text-secondary">
											{sampleLabel(candidate.sampleId)}
										</td>
										<td className="px-3 py-3 text-text-secondary">
											dataset_id、sample_id、target_stage、failure_type、responsible_asset、actual_result_ref
										</td>
										<td className="px-3 py-3 font-mono text-xs text-text-secondary">{lifecycle}</td>
										<td className="px-3 py-3 text-text-secondary">{candidate.failure}</td>
										<td className="px-3 py-3 text-text-secondary">{candidate.asset}</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>

				<div className="flex flex-col gap-2 rounded-md border border-surface-border bg-surface-background px-3 py-3 text-sm text-text-secondary md:flex-row md:items-center md:justify-between">
					<span>
						边界：不新增接口或数据库、不自动提交 EvalRun、不自动生成
						Badcase、不写回回归集；当前只做请求草案和状态门预检。
					</span>
					<a
						href="/test-datasets?mock=1#p10-single-stage-evalrun-draft-lifecycle"
						className="inline-flex w-fit items-center gap-1 rounded-md border border-surface-border bg-surface-card px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
					>
						回到草案口径
						<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
					</a>
				</div>
			</CardContent>
		</Card>
	);
}

function SingleStageSubmitReadinessPanel({
	selection,
}: {
	selection: SingleStageSelection;
}) {
	const selectedRow = SINGLE_STAGE_SUBMIT_READINESS_ROWS.find(
		(row) =>
			selection.source === "p10_single_stage" &&
			selection.candidateMode === "manual" &&
			selection.draftMode === "eval_run" &&
			selection.readinessMode === "submit_ready" &&
			row.sampleId === selection.sampleId &&
			row.stageId === selection.targetStage,
	);

	return (
		<Card
			id="single-stage-submit-readiness"
			className="scroll-mt-6"
			data-testid="m07-single-stage-submit-readiness"
		>
			<CardHeader className="space-y-2">
				<div className="flex items-start gap-3">
					<span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-surface-subtle text-brand-primary">
						<ShieldCheck className="h-4 w-4" aria-hidden="true" />
					</span>
					<div>
						<div className="flex flex-wrap items-center gap-2">
							<CardTitle className="text-lg">提交前检查</CardTitle>
							<span className="rounded border border-brand-warning/30 bg-brand-warning/10 px-1.5 py-0.5 text-[11px] font-semibold text-brand-warning">
								不调用 EvalRun 提交接口
							</span>
						</div>
						<CardDescription className="mt-1 leading-6">
							承接评测运行草案，只判断哪些单环节草案经过人工确认后可进入提交候选；不自动提交
							EvalRun，不自动生成 Badcase。
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="rounded-md border border-surface-border bg-surface-subtle px-3 py-3 text-sm text-text-secondary">
					<div className="font-medium text-text-primary">
						{selectedRow
							? singleStageSelectionLabel(selectedRow.sampleId, selectedRow.label)
							: "等待从 EvalRun 草案带入 submit-ready 预检"}
					</div>
					<p className="mt-2 leading-6">
						当前只展示提交前门禁和阻塞原因：候选环节可进入 manual_reviewed -&gt;
						submit_ready；工具、知识、提示词和质量策略在自动判分合同补齐前仍保持阻塞。
					</p>
				</div>

				<div className="overflow-x-auto rounded-md border border-surface-border">
					<table className="w-full min-w-[1120px] border-collapse text-sm">
						<thead className="bg-surface-subtle text-left text-xs text-text-muted">
							<tr>
								<th className="px-3 py-2 font-medium">环节</th>
								<th className="px-3 py-2 font-medium">样本</th>
								<th className="px-3 py-2 font-medium">门禁状态</th>
								<th className="px-3 py-2 font-medium">人工确认项</th>
								<th className="px-3 py-2 font-medium">允许状态</th>
								<th className="px-3 py-2 font-medium">责任资产</th>
								<th className="px-3 py-2 font-medium">下一步</th>
							</tr>
						</thead>
						<tbody>
							{SINGLE_STAGE_SUBMIT_READINESS_ROWS.map((row) => {
								const active = selectedRow?.stageId === row.stageId;
								return (
									<tr
										key={row.stageId}
										className={`border-t border-surface-border align-top ${
											active ? "bg-brand-warning/10" : ""
										}`}
										data-testid="m07-single-stage-submit-readiness-row"
									>
										<td className="px-3 py-3">
											<div className="font-medium text-text-primary">{row.label}</div>
											<div className="mt-1 font-mono text-xs text-text-muted">{row.stageId}</div>
										</td>
										<td className="px-3 py-3 text-xs text-text-secondary">
											{sampleLabel(row.sampleId)}
										</td>
										<td className="px-3 py-3 font-mono text-xs font-medium text-text-primary">
											{row.readinessState}
										</td>
										<td className="px-3 py-3 text-text-secondary">{row.confirmations}</td>
										<td className="px-3 py-3 font-mono text-xs text-text-secondary">
											{row.transition}
										</td>
										<td className="px-3 py-3 text-text-secondary">{row.asset}</td>
										<td className="px-3 py-3 text-text-secondary">{row.next}</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>

				<div className="flex flex-col gap-2 rounded-md border border-surface-border bg-surface-background px-3 py-3 text-sm text-text-secondary md:flex-row md:items-center md:justify-between">
					<span>
						边界：不调用 EvalRun 提交接口、不自动生成
						Badcase、不写回回归集；后续真实提交需要单独接口、字段、审计和回归验证合同。
					</span>
					<a
						href="/test-datasets?mock=1#p10-single-stage-submit-readiness-gate"
						className="inline-flex w-fit items-center gap-1 rounded-md border border-surface-border bg-surface-card px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
					>
						回到门禁口径
						<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
					</a>
				</div>
			</CardContent>
		</Card>
	);
}

function SingleStageSubmitContractPanel({
	selection,
}: {
	selection: SingleStageSelection;
}) {
	const selectedRow = SINGLE_STAGE_SUBMIT_CONTRACT_ROWS.find(
		(row) =>
			selection.source === "p10_single_stage" &&
			selection.candidateMode === "manual" &&
			selection.draftMode === "eval_run" &&
			selection.readinessMode === "submit_ready" &&
			selection.submitMode === "eval_run_request" &&
			row.sampleId === selection.sampleId &&
			row.stageId === selection.targetStage,
	);

	return (
		<Card
			id="single-stage-submit-contract"
			className="scroll-mt-6"
			data-testid="m07-single-stage-submit-contract"
		>
			<CardHeader className="space-y-2">
				<div className="flex items-start gap-3">
					<span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-surface-subtle text-brand-primary">
						<ClipboardList className="h-4 w-4" aria-hidden="true" />
					</span>
					<div>
						<div className="flex flex-wrap items-center gap-2">
							<CardTitle className="text-lg">提交请求预览</CardTitle>
							<span className="rounded border border-brand-warning/30 bg-brand-warning/10 px-1.5 py-0.5 text-[11px] font-semibold text-brand-warning">
								不调用 EvalRun 提交接口
							</span>
						</div>
						<CardDescription className="mt-1 leading-6">
							承接提交候选，只展示评测运行请求和审核前置； 当前不自动提交 EvalRun，不自动生成
							Badcase。
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="rounded-md border border-surface-border bg-surface-subtle px-3 py-3 text-sm text-text-secondary">
					<div className="font-medium text-text-primary">
						{selectedRow
							? singleStageSelectionLabel(selectedRow.sampleId, selectedRow.label)
							: "等待从 submit-ready 门禁带入提交请求预检"}
					</div>
					<p className="mt-2 leading-6">
						当前只准备人工可审核请求：意图分类使用 mode=intent，处理方案使用
						mode=playbook；工具、知识、提示词和质量策略先保留人工复核，不生成 EvalRunCreateRequest。
					</p>
				</div>

				<div className="overflow-x-auto rounded-md border border-surface-border">
					<table className="w-full min-w-[1120px] border-collapse text-sm">
						<thead className="bg-surface-subtle text-left text-xs text-text-muted">
							<tr>
								<th className="px-3 py-2 font-medium">环节</th>
								<th className="px-3 py-2 font-medium">样本</th>
								<th className="px-3 py-2 font-medium">提交状态</th>
								<th className="px-3 py-2 font-medium">请求体</th>
								<th className="px-3 py-2 font-medium">审核项</th>
								<th className="px-3 py-2 font-medium">责任资产</th>
								<th className="px-3 py-2 font-medium">下一步</th>
							</tr>
						</thead>
						<tbody>
							{SINGLE_STAGE_SUBMIT_CONTRACT_ROWS.map((row) => {
								const active = selectedRow?.stageId === row.stageId;
								return (
									<tr
										key={row.stageId}
										className={`border-t border-surface-border align-top ${
											active ? "bg-brand-warning/10" : ""
										}`}
										data-testid="m07-single-stage-submit-contract-row"
									>
										<td className="px-3 py-3">
											<div className="font-medium text-text-primary">{row.label}</div>
											<div className="mt-1 font-mono text-xs text-text-muted">{row.stageId}</div>
										</td>
										<td className="px-3 py-3 text-xs text-text-secondary">
											{sampleLabel(row.sampleId)}
										</td>
										<td className="px-3 py-3 font-mono text-xs font-medium text-text-primary">
											{row.submitState}
										</td>
										<td className="px-3 py-3 text-text-secondary">{row.request}</td>
										<td className="px-3 py-3 text-text-secondary">{row.review}</td>
										<td className="px-3 py-3 text-text-secondary">{row.asset}</td>
										<td className="px-3 py-3 text-text-secondary">{row.next}</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>

				<div className="flex flex-col gap-2 rounded-md border border-surface-border bg-surface-background px-3 py-3 text-sm text-text-secondary md:flex-row md:items-center md:justify-between">
					<span>
						边界：只准备 EvalRunCreateRequest 兼容请求，不调用 EvalRun 提交接口、不自动生成
						Badcase、不写回回归集；真实提交和状态回写仍需后续合同。
					</span>
					<a
						href="/test-datasets?mock=1#p10-single-stage-submit-contract"
						className="inline-flex w-fit items-center gap-1 rounded-md border border-surface-border bg-surface-card px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
					>
						回到请求合同
						<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
					</a>
				</div>
			</CardContent>
		</Card>
	);
}

function SingleStageSubmitGatePanel({
	selection,
}: {
	selection: SingleStageSelection;
}) {
	const createEvalRun = useCreateEvalRun();
	const selectedRow = SINGLE_STAGE_MANUAL_SUBMIT_GATE_ROWS.find(
		(row) =>
			selection.source === "p10_single_stage" &&
			selection.candidateMode === "manual" &&
			selection.draftMode === "eval_run" &&
			selection.readinessMode === "submit_ready" &&
			selection.submitMode === "eval_run_request" &&
			selection.executionMode === "manual_submit_gate" &&
			row.sampleId === selection.sampleId &&
			row.stageId === selection.targetStage,
	);
	const submitRequest =
		selectedRow && "submitRequest" in selectedRow ? selectedRow.submitRequest : undefined;
	const canSubmit = submitRequest !== undefined;
	const receipt = createEvalRun.data;

	function handleManualSubmit() {
		if (submitRequest === undefined) {
			return;
		}
		createEvalRun.mutate(submitRequest);
	}

	return (
		<Card
			id="single-stage-submit-gate"
			className="scroll-mt-6"
			data-testid="m07-single-stage-submit-gate"
		>
			<CardHeader className="space-y-2">
				<div className="flex items-start gap-3">
					<span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-surface-subtle text-brand-primary">
						<ShieldCheck className="h-4 w-4" aria-hidden="true" />
					</span>
					<div>
						<div className="flex flex-wrap items-center gap-2">
							<CardTitle className="text-lg">人工提交执行</CardTitle>
							<span className="rounded border border-brand-success/30 bg-brand-success/10 px-1.5 py-0.5 text-[11px] font-semibold text-brand-success">
								复用现有 EvalRun 接口
							</span>
						</div>
						<CardDescription className="mt-1 leading-6">
							承接提交请求预览，只定义人工确认后可否调用现有 POST
							/admin/v1/eval/runs，以及提交后的状态回写和边界检查；当前不自动提交、不自动生成
							Badcase。
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="rounded-md border border-surface-border bg-surface-subtle px-3 py-3 text-sm text-text-secondary">
					<div className="font-medium text-text-primary">
						{selectedRow
							? singleStageSelectionLabel(selectedRow.sampleId, selectedRow.label)
							: "等待从提交请求预检带入人工提交执行门"}
					</div>
					<p className="mt-2 leading-6">
						意图分类和处理方案是 manual_submit_allowed，可在人工确认后复用现有 EvalRun
						接口；工具、知识、提示词和质量策略仍保持人工复核或合同阻塞。
					</p>
				</div>

				<div className="flex flex-col gap-3 rounded-md border border-surface-border bg-surface-background px-3 py-3 md:flex-row md:items-center md:justify-between">
					<div className="text-sm text-text-secondary">
						<div className="font-medium text-text-primary">人工提交动作</div>
						<p className="mt-1 leading-6">
							{canSubmit
								? "确认当前样本和环节后，人工触发一次现有 EvalRun 运行。"
								: "当前没有可提交请求；该环节仍需先补齐自动判分或字段合同。"}
						</p>
					</div>
					<Button
						type="button"
						size="sm"
						onClick={handleManualSubmit}
						disabled={!canSubmit || createEvalRun.isPending}
					>
						{createEvalRun.isPending ? "提交中..." : "人工提交 EvalRun"}
					</Button>
				</div>

				{createEvalRun.isError ? (
					<div className="rounded-md border border-brand-danger/30 bg-brand-danger/10 px-3 py-3 text-sm text-brand-danger">
						EvalRun 提交失败，请保留当前样本、环节和请求信息后排查接口返回。
					</div>
				) : null}

				{receipt ? (
					<>
						<SingleStageSubmitReceipt receipt={receipt} />
						<SingleStageBadcaseDecisionGate receipt={receipt} selection={selection} />
						<SingleStageBadcaseAuditContract receipt={receipt} selection={selection} />
					</>
				) : null}

				<div className="overflow-x-auto rounded-md border border-surface-border">
					<table className="w-full min-w-[1120px] border-collapse text-sm">
						<thead className="bg-surface-subtle text-left text-xs text-text-muted">
							<tr>
								<th className="px-3 py-2 font-medium">环节</th>
								<th className="px-3 py-2 font-medium">样本</th>
								<th className="px-3 py-2 font-medium">执行门状态</th>
								<th className="px-3 py-2 font-medium">调用接口</th>
								<th className="px-3 py-2 font-medium">状态回写</th>
								<th className="px-3 py-2 font-medium">提交后检查</th>
								<th className="px-3 py-2 font-medium">下一步</th>
							</tr>
						</thead>
						<tbody>
							{SINGLE_STAGE_MANUAL_SUBMIT_GATE_ROWS.map((row) => {
								const active = selectedRow?.stageId === row.stageId;
								return (
									<tr
										key={row.stageId}
										className={`border-t border-surface-border align-top ${
											active ? "bg-brand-success/10" : ""
										}`}
										data-testid="m07-single-stage-submit-gate-row"
									>
										<td className="px-3 py-3">
											<div className="font-medium text-text-primary">{row.label}</div>
											<div className="mt-1 font-mono text-xs text-text-muted">{row.stageId}</div>
										</td>
										<td className="px-3 py-3 text-xs text-text-secondary">
											{sampleLabel(row.sampleId)}
										</td>
										<td className="px-3 py-3 font-mono text-xs font-medium text-text-primary">
											{row.submitGateState}
										</td>
										<td className="px-3 py-3 font-mono text-xs text-text-secondary">
											{row.endpoint}
										</td>
										<td className="px-3 py-3 font-mono text-xs text-text-secondary">
											{row.transition}
										</td>
										<td className="px-3 py-3 text-text-secondary">
											{row.check}
											<div className="mt-1 text-xs text-text-muted">{row.asset}</div>
										</td>
										<td className="px-3 py-3 text-text-secondary">{row.next}</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>

				<div className="flex flex-col gap-2 rounded-md border border-surface-border bg-surface-background px-3 py-3 text-sm text-text-secondary md:flex-row md:items-center md:justify-between">
					<span>
						边界：复用现有 POST /admin/v1/eval/runs，不新增 endpoint/DB/OpenAPI；当前不自动提交
						EvalRun、不自动生成 Badcase、不写回回归集。
					</span>
					<a
						href="/test-datasets?mock=1#p10-single-stage-submit-gate"
						className="inline-flex w-fit items-center gap-1 rounded-md border border-surface-border bg-surface-card px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
					>
						回到执行门
						<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
					</a>
				</div>
			</CardContent>
		</Card>
	);
}

function SingleStageSubmitReceipt({ receipt }: { receipt: EvalRunResponse }) {
	return (
		<div
			className="rounded-md border border-brand-success/30 bg-brand-success/10 px-3 py-3 text-sm text-brand-success"
			data-testid="m07-single-stage-submit-receipt"
		>
			<div className="flex items-center gap-2 font-medium">
				<CheckCircle2 className="h-4 w-4" aria-hidden="true" />
				<span>已提交 EvalRun</span>
			</div>
			<div className="mt-2 grid gap-2 text-xs md:grid-cols-3">
				<div>
					<span className="text-brand-success">run_id</span>
					<div className="mt-1 font-mono text-brand-success">{receipt.eval_run_id}</div>
				</div>
				<div>
					<span className="text-brand-success">status</span>
					<div className="mt-1 font-mono text-brand-success">{receipt.status}</div>
				</div>
				<div>
					<span className="text-brand-success">request</span>
					<div className="mt-1 font-mono text-brand-success">
						mode={receipt.mode}；dataset={receipt.dataset_id}
					</div>
				</div>
			</div>
			<div className="mt-2 text-xs leading-6 text-brand-success">
				samples={receipt.summary.total}；只写入 EvalRun / EvalSampleResult，不自动生成
				Badcase，不写回回归集。
			</div>
			<div className="mt-3 rounded-md border border-brand-success/30 bg-white/70 px-3 py-2">
				<div className="text-xs font-semibold text-brand-success">提交后追踪</div>
				<ul className="mt-2 grid gap-1.5 text-xs leading-5 text-brand-success md:grid-cols-3">
					<li>运行历史已刷新，可按 run_id 回看本次单环节 EvalRun。</li>
					<li>Badcase 自动候选未开启，失败样本仍需人工复核。</li>
					<li>回归写回未开启，通过后续门禁再进入回归集。</li>
				</ul>
			</div>
		</div>
	);
}

function SingleStageBadcaseDecisionGate({
	receipt,
	selection,
}: {
	receipt: EvalRunResponse;
	selection: SingleStageSelection;
}) {
	const selectedRow = SINGLE_STAGE_BADCASE_DECISION_ROWS.find(
		(row) => row.sampleId === selection.sampleId && row.stageId === selection.targetStage,
	);

	return (
		<div
			className="rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-3"
			data-testid="m07-single-stage-badcase-decision-gate"
		>
			<div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
				<div>
					<div className="flex flex-wrap items-center gap-2">
						<div className="text-sm font-semibold text-brand-warning">问题闭环决策</div>
						<span className="rounded border border-brand-warning/30 bg-white/70 px-1.5 py-0.5 text-[11px] font-semibold text-brand-warning">
							人工复核
						</span>
					</div>
					<p className="mt-1 text-xs leading-5 text-brand-warning">
						提交后的 EvalRun
						先进入人工决策门：判断失败是否可复现、责任资产是否成立，再决定是否进入现有 Badcase
						流转。
					</p>
				</div>
				<div className="rounded border border-brand-warning/30 bg-white/70 px-2.5 py-1.5 text-xs text-brand-warning">
					EvalRun 当前候选 {receipt.summary.badcase_candidate_count} 条候选
				</div>
			</div>

			<div className="mt-3 overflow-x-auto rounded-md border border-brand-warning/30 bg-white/70">
				<table className="w-full min-w-[960px] border-collapse text-xs">
					<thead className="bg-brand-warning/15 text-left text-brand-warning">
						<tr>
							<th className="px-3 py-2 font-medium">环节</th>
							<th className="px-3 py-2 font-medium">样本</th>
							<th className="px-3 py-2 font-medium">决策状态</th>
							<th className="px-3 py-2 font-medium">证据来源</th>
							<th className="px-3 py-2 font-medium">人工复核</th>
							<th className="px-3 py-2 font-medium">下一步</th>
						</tr>
					</thead>
					<tbody>
						{SINGLE_STAGE_BADCASE_DECISION_ROWS.map((row) => {
							const active = selectedRow?.stageId === row.stageId;
							return (
								<tr
									key={row.stageId}
									className={`border-t border-brand-warning/30 align-top ${
										active ? "bg-brand-warning/15" : ""
									}`}
								>
									<td className="px-3 py-2">
										<div className="font-medium text-brand-warning">{row.label}</div>
										<div className="mt-1 font-mono text-[11px] text-brand-warning">
											{row.stageId}
										</div>
									</td>
									<td className="px-3 py-2 text-[11px] text-brand-warning">
										{sampleLabel(row.sampleId)}
									</td>
									<td className="px-3 py-2 font-mono text-[11px] font-medium text-brand-warning">
										{row.decisionState}
									</td>
									<td className="px-3 py-2 leading-5 text-brand-warning">{row.evidence}</td>
									<td className="px-3 py-2 leading-5 text-brand-warning">{row.review}</td>
									<td className="px-3 py-2 leading-5 text-brand-warning">{row.next}</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			<div className="mt-3 text-xs leading-5 text-brand-warning">
				边界：不自动创建
				Badcase、不写回回归集、不新增接口或数据库；本轮只补提交后的人工决策门和可审核口径。
			</div>
		</div>
	);
}

function SingleStageBadcaseAuditContract({
	receipt,
	selection,
}: {
	receipt: EvalRunResponse;
	selection: SingleStageSelection;
}) {
	const decisionRow = SINGLE_STAGE_BADCASE_DECISION_ROWS.find(
		(row) => row.sampleId === selection.sampleId && row.stageId === selection.targetStage,
	);
	const responsibleAsset =
		selection.targetStage === "playbook_routing"
			? "退款处理方案（PB02_refund）、分类到方案映射"
			: selection.targetStage === "intent_classification"
				? "意图分类器、分类规则"
				: selection.targetStage === "tool_and_evidence"
					? "知识检索（SK05）、政策知识库、证据注入"
					: selection.targetStage === "knowledge_retrieval"
						? "政策知识库、知识检索、证据注入"
						: selection.targetStage === "prompt_response"
							? "主回复提示词、证据注入、方案引导"
							: selection.targetStage === "quality_guard"
								? "质量策略、判断规则、R2 写操作保护"
								: "待人工确认";

	return (
		<div
			className="rounded-md border border-brand-info/30 bg-brand-info/10 px-3 py-3"
			data-testid="m07-single-stage-badcase-audit-contract"
		>
			<div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
				<div>
					<div className="flex flex-wrap items-center gap-2">
						<div className="text-sm font-semibold text-brand-info">决策审计记录</div>
						<span className="rounded border border-brand-info/30 bg-white/70 px-1.5 py-0.5 text-[11px] font-semibold text-brand-info">
							decision_audit_required
						</span>
					</div>
					<p className="mt-1 text-xs leading-5 text-brand-info">
						把人工决策继续收成审计记录：先定义未来要记录哪些决策事件和字段，再进入 Badcase
						自动候选或回归写回门禁。
					</p>
				</div>
				<div className="rounded border border-brand-info/30 bg-white/70 px-2.5 py-1.5 text-xs text-brand-info">
					run_id <span className="font-mono">{receipt.eval_run_id}</span>
				</div>
			</div>

			<div className="mt-3 grid gap-2 text-xs md:grid-cols-3">
				<div className="rounded border border-brand-info/30 bg-white/70 px-2.5 py-2">
					<div className="font-medium text-brand-info">样本与环节</div>
					<div className="mt-1 text-brand-info">
						{selection.sampleId ? sampleLabel(selection.sampleId) : "待选择"} /{" "}
						{selection.targetStage ? targetStageLabel(selection.targetStage) : "待选择"}
					</div>
				</div>
				<div className="rounded border border-brand-info/30 bg-white/70 px-2.5 py-2">
					<div className="font-medium text-brand-info">责任资产</div>
					<div className="mt-1 text-brand-info">{responsibleAsset}</div>
				</div>
				<div className="rounded border border-brand-info/30 bg-white/70 px-2.5 py-2">
					<div className="font-medium text-brand-info">决策来源</div>
					<div className="mt-1 text-brand-info">
						{decisionRow?.decisionState ?? "blocked_by_missing_evidence"}
					</div>
				</div>
			</div>

			<div className="mt-3 overflow-x-auto rounded-md border border-brand-info/30 bg-white/70">
				<table className="w-full min-w-[920px] border-collapse text-xs">
					<thead className="bg-brand-info/15 text-left text-brand-info">
						<tr>
							<th className="px-3 py-2 font-medium">事件</th>
							<th className="px-3 py-2 font-medium">触发条件</th>
							<th className="px-3 py-2 font-medium">必备字段</th>
							<th className="px-3 py-2 font-medium">承接页面</th>
						</tr>
					</thead>
					<tbody>
						{SINGLE_STAGE_BADCASE_AUDIT_EVENTS.map((event) => (
							<tr key={event.eventName} className="border-t border-brand-info/30 align-top">
								<td className="px-3 py-2 font-mono text-[11px] font-medium text-brand-info">
									{event.eventName}
								</td>
								<td className="px-3 py-2 leading-5 text-brand-info">{event.trigger}</td>
								<td className="px-3 py-2 leading-5 text-brand-info">{event.requiredFields}</td>
								<td className="px-3 py-2 leading-5 text-brand-info">{event.owner}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<div className="mt-3 text-xs leading-5 text-brand-info">
				边界：只记录审计合同，不自动生成 Badcase，不写入 BadcaseEvent，不新增
				endpoint/DB/OpenAPI；后续如要落事件或自动候选，需要独立接口与字段合同。
			</div>
		</div>
	);
}

function getSingleStageManualCandidateSelection() {
	if (typeof window === "undefined") {
		return {
			source: null,
			sampleId: null,
			targetStage: null,
			candidateMode: null,
			draftMode: null,
			readinessMode: null,
			submitMode: null,
			executionMode: null,
		};
	}
	const params = new URLSearchParams(window.location.search);
	return {
		source: params.get("source"),
		sampleId: params.get("sample_id"),
		targetStage: params.get("target_stage"),
		candidateMode: params.get("candidate_mode"),
		draftMode: params.get("draft_mode"),
		readinessMode: params.get("readiness_mode"),
		submitMode: params.get("submit_mode"),
		executionMode: params.get("execution_mode"),
	};
}
